import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';
import {
  asArray,
  directChildSpaces,
  encodeRef,
  entityNamesByRefid,
  isLive,
} from '@repo/front-end-utils';

/**
 * Why a GENERATED parent-space total needs a person to look at it
 * (`complex-measures-in-configuration-parent-space-composites`, FR11a; Rob, 2026-10-01).
 *
 * A generated total adds up child SPACES only. Two shapes are unusual enough to point out:
 *
 * - **Equipment standing directly in the space.** A section with two areas and a machine is totalled
 *   over the two areas; the machine is not in the sum. That may be right — it may be measured some
 *   other way — or it may be a gap, and only a person can tell.
 * - **A floor the twin's total cannot include**, because it has no usable name. Go Live cannot file a
 *   total naming such a floor, so it is left out rather than blocking the launch.
 *
 * **It blocks nothing and is never stored.** It is derived from the plan on every render, so it
 * clears itself the moment the equipment moves or the floor is named; and only generated totals are
 * assessed, so editing one (which makes it hand-made) also clears it. ONE rule decides which totals
 * need review — `needsReview`, read by the stage's sentences and by Launch Twin's list alike — so the
 * two cannot disagree about WHICH totals; the per-total sentences are the stage's alone, and Launch
 * words its list with one general sentence.
 */

const listed = (names: string[]): string => {
  const shown = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${shown}, +${names.length - 3} more` : shown;
};

/**
 * Review sentences by composite refid, for every live generated composite that has any.
 *
 * `labelOf` names the composite's target in the stage's own words (the picker's label), so a
 * sentence reads the same as the row it sits under.
 */
const needsReview = (
  composite: FloorPlannerComposite,
  hierarchy: ReturnType<typeof directChildSpaces>,
): boolean =>
  (hierarchy.equipment.get(encodeRef(composite.target)) ?? []).length > 0 ||
  (composite.target?.kind === 'twin' && hierarchy.unfileableFloorIds.length > 0);

/** The refids of the live generated totals that need review — no wording. What Launch lists. */
export const generatedReviewRefids = (
  layout: FloorPlannerLayout | null | undefined,
): Set<string> => {
  const refids = new Set<string>();
  if (!layout) return refids;
  const hierarchy = directChildSpaces(layout);
  for (const composite of asArray(layout.composites).filter(isLive)) {
    if (composite.generated === true && needsReview(composite, hierarchy)) {
      refids.add(composite.refid);
    }
  }
  return refids;
};

export const generatedReviews = (
  layout: FloorPlannerLayout | null | undefined,
  labelOf: (ref: FloorPlannerCompositeRef) => string,
): Map<string, string[]> => {
  const reviews = new Map<string, string[]>();
  if (!layout) return reviews;
  const hierarchy = directChildSpaces(layout);
  const names = entityNamesByRefid(layout);

  for (const composite of asArray(layout.composites).filter(isLive)) {
    if ((composite as FloorPlannerComposite).generated !== true) continue;
    const sentences: string[] = [];
    const label = labelOf(composite.target);

    const equipment = hierarchy.equipment.get(encodeRef(composite.target)) ?? [];
    if (equipment.length > 0) {
      const n = equipment.length;
      sentences.push(
        `${label} also holds ${n} piece${n === 1 ? '' : 's'} of equipment that this generated ` +
          `total does not add up: ${listed(equipment.map(i => names.get(i.refid) ?? 'unnamed equipment'))}. ` +
          `Check whether ${n === 1 ? 'it is' : 'they are'} measured some other way.`,
      );
    }

    if (composite.target?.kind === 'twin' && hierarchy.unfileableFloorIds.length > 0) {
      const n = hierarchy.unfileableFloorIds.length;
      sentences.push(
        `${label} has ${n} floor${n === 1 ? '' : 's'} with no usable name — blank, or the same as ` +
          `another floor's — which this generated total does not add up. Give ` +
          `${n === 1 ? 'it' : 'each'} a name of its own on the Floor Plan to include ` +
          `${n === 1 ? 'it' : 'them'}.`,
      );
    }

    if (sentences.length > 0) reviews.set(composite.refid, sentences);
  }
  return reviews;
};
